/**
 * Today's area row (today-phone-area-row): which cells show on a phone, how each area is
 * drawn, and letter ink that reads on the cell in both themes.
 */

export interface TodayAreaOption {
    id: string;
    label: string;
    /** Hex color of the area (its configured color, else the built-in palette's). */
    color: string;
}

/** 24-unit stroke paths. The seven built-in areas, All areas and More. */
export const AREA_LINE_ICON_PATHS: Record<string, string> = {
    all: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
    job_hunt: 'M5 7h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zM9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2',
    finance: 'M12 3v18M16 7.5c0-1.9-1.8-3-4-3s-4 1.1-4 3 1.8 2.6 4 3.2 4 1.3 4 3.3-1.8 3-4 3-4-1.1-4-3',
    parenthood: 'M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z',
    business: 'M6 3h12a1 1 0 0 1 1 1v17H5V4a1 1 0 0 1 1-1zM9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2',
    personal: 'M12 12a4 4 0 1 0 0-8a4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0',
    health: 'M3 12h4l2-5 4 10 2-5h6',
    general: 'M21 12a9 9 0 1 1-18 0a9 9 0 1 1 18 0M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18',
};

export const MORE_ICON_PATH = 'M5 12h.01M12 12h.01M19 12h.01';

const BUILT_IN_AREA_KEYS = new Set(['job_hunt', 'finance', 'parenthood', 'business', 'personal', 'health', 'general']);

export function isBuiltInArea(id: string): boolean {
    return BUILT_IN_AREA_KEYS.has(id);
}

/** CSS token holding a built-in area's ink (both themes in index.css). */
export function builtInInkVar(id: string): string {
    // All areas uses the text shade. The accent itself falls under 3:1 on the light selected cell.
    return id === 'all' ? 'var(--color-accent-text)' : `var(--color-area-${id.replace(/_/g, '-')})`;
}

/** Most cells a phone row holds: All areas, three areas, More. */
export const PHONE_ROW_MAX_CELLS = 5;

export type TodayAreaRowCell =
    | { kind: 'area'; option: TodayAreaOption }
    | { kind: 'more'; hidden: TodayAreaOption[]; selectedHidden: TodayAreaOption | null };

/**
 * `options` starts with All areas, in display order. Four areas or fewer: one cell each.
 * More than four: All areas, the first three, then More (which carries the selected area
 * when it is one of the hidden ones).
 */
export function buildPhoneAreaRow(options: TodayAreaOption[], selectedId: string | null): TodayAreaRowCell[] {
    if (options.length <= PHONE_ROW_MAX_CELLS) {
        return options.map((option) => ({ kind: 'area', option }));
    }
    const shown = options.slice(0, PHONE_ROW_MAX_CELLS - 1);
    const hidden = options.slice(PHONE_ROW_MAX_CELLS - 1);
    const selectedHidden = hidden.find((o) => o.id === selectedId) ?? null;
    return [...shown.map((option) => ({ kind: 'area' as const, option })), { kind: 'more', hidden, selectedHidden }];
}

/** First Unicode letter or digit. Skips emoji, punctuation, and lone surrogates. */
function letterOrDigit(word: string): string {
    return word.match(/[\p{L}\p{N}]/u)?.[0] ?? '';
}

function firstLetters(label: string): { one: string; two: string } {
    // NFC first: a base letter typed with a separate accent ("E" + U+0301) keeps its accent.
    const words = label.normalize('NFC').trim().split(/\s+/).filter((w) => letterOrDigit(w));
    const first = words[0] ?? '';
    const one = (letterOrDigit(first) || '?').toUpperCase();
    const rest = (first.match(/[\p{L}\p{N}]/gu) ?? [])[1] ?? '';
    const two = words.length >= 2
        ? `${one}${letterOrDigit(words[1]).toUpperCase()}`
        : `${one}${rest.toLowerCase()}` || one;
    return { one, two };
}

/**
 * Letter marks for areas you made: the first letter, or two when another of your areas
 * starts with the same letter ("Housing Project" → "HP", "Taxes" → "Ta").
 */
export function areaLetterMarks(options: TodayAreaOption[]): Record<string, string> {
    const own = options.filter((o) => o.id !== 'all' && !isBuiltInArea(o.id));
    const counts = new Map<string, number>();
    const letters = own.map((o) => ({ id: o.id, ...firstLetters(o.label) }));
    for (const l of letters) counts.set(l.one, (counts.get(l.one) ?? 0) + 1);
    const marks: Record<string, string> = {};
    for (const l of letters) marks[l.id] = (counts.get(l.one) ?? 0) > 1 ? l.two : l.one;
    return marks;
}

function parseHex(hex: string): [number, number, number] | null {
    const m = hex.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (!m) return null;
    const h = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}

function toHex([r, g, b]: [number, number, number]): string {
    return `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

function luminance([r, g, b]: [number, number, number]): number {
    const f = (c: number) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

export function contrastRatio(a: string, b: string): number {
    const ca = parseHex(a);
    const cb = parseHex(b);
    if (!ca || !cb) return 1;
    const [hi, lo] = [luminance(ca), luminance(cb)].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
}

/** The cell under each mark: `--color-bg-tertiary` in index.css, dark then light. */
export const AREA_CELL_BG = { dark: '#334155', light: '#E2E8F0' } as const;

/** Selected cell lays this much of the area color over the cell. */
export const SELECTED_TINT_ALPHA = 0.18;

type Rgb = [number, number, number];

/** `over` at `alpha` on `under`, the same composite the selected cell paints. */
export function compositeOver(underHex: string, overHex: string, alpha: number): Rgb | null {
    const under = parseHex(underHex);
    const over = parseHex(overHex);
    if (!under || !over) return null;
    return under.map((c, i) => c * (1 - alpha) + over[i] * alpha) as Rgb;
}

export function contrastRatioRgb(inkHex: string, bg: Rgb): number {
    const ink = parseHex(inkHex);
    if (!ink) return 1;
    const [hi, lo] = [luminance(ink), luminance(bg)].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
}

/**
 * Moves `color` toward white (on a dark cell) or black (on a light one) in 5% steps until
 * the rounded hex reads at `min` against the plain cell and against that cell with the
 * area color at 18% over it, both exact and rounded to whole channels (a browser may paint
 * either). Unparseable colors fall back to the theme's text color.
 */
export function inkWithContrast(color: string, bg: string, min = 4.5): string {
    const base = parseHex(color);
    const under = parseHex(bg);
    if (!under) return color;
    const target: Rgb = luminance(under) < 0.5 ? [255, 255, 255] : [0, 0, 0];
    if (!base) return toHex(target);
    const tinted = compositeOver(bg, color, SELECTED_TINT_ALPHA);
    const painted = tinted && (tinted.map(Math.round) as Rgb);
    for (let step = 0; step <= 20; step += 1) {
        const t = step / 20;
        const mixed = base.map((c, i) => c + (target[i] - c) * t) as Rgb;
        const hex = toHex(mixed);
        const onCell = contrastRatio(hex, bg) >= min;
        const onSelected = !tinted || !painted
            || (contrastRatioRgb(hex, tinted) >= min && contrastRatioRgb(hex, painted) >= min);
        if (onCell && onSelected) return hex;
    }
    return toHex(target);
}

/**
 * Selected cell: the 18% tint sits on `--color-bg-tertiary`, so a phone cell
 * (no row background behind it) reads the same as a desktop cell.
 */
export function selectedCellBackground(tintColor: string): { backgroundColor: string; backgroundImage: string } {
    return {
        backgroundColor: 'var(--color-bg-tertiary)',
        backgroundImage: `linear-gradient(${tintColor}, ${tintColor})`,
    };
}

/** `rgba()` of a hex color, for the selected cell's tint. */
export function tint(color: string, alpha: number): string {
    const c = parseHex(color);
    return c ? `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${alpha})` : 'transparent';
}
